<template>
  <section v-if="hasMetrics" class="enhanced-viral-section">
    <h2>🔥 增强爆款分析</h2>
    
    <div class="metrics-grid">
      <!-- 开场钩子强度 -->
      <div class="metric-card hook-card">
        <div class="metric-header">
          <h3>🎣 开场钩子强度</h3>
        </div>
        <div class="metric-body">
          <div class="score-display">
            <div class="score-ring">
              <svg viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" class="ring-bg" />
                <circle 
                  cx="50" cy="50" r="45" 
                  class="ring-progress"
                  :style="{ strokeDashoffset: getRingOffset(hookStrength || 0, 10) }"
                />
              </svg>
              <div class="score-text">
                <span class="score-number">{{ hookStrength || 0 }}</span>
                <span class="score-max">/10</span>
              </div>
            </div>
            <div class="score-label">{{ getHookLevel(hookStrength || 0) }}</div>
          </div>
          <p class="metric-description">{{ hookAnalysis }}</p>
        </div>
      </div>

      <!-- 节奏密度 -->
      <div class="metric-card rhythm-card">
        <div class="metric-header">
          <h3>🎵 节奏密度</h3>
        </div>
        <div class="metric-body">
          <div class="rhythm-chart">
            <canvas ref="rhythmCanvas" height="120"></canvas>
          </div>
          <div class="rhythm-stats">
            <div class="stat-item">
              <span class="stat-label">评分</span>
              <span class="stat-value">{{ rhythmDensity }}/10</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">切换频率</span>
              <span class="stat-value">{{ cutFrequency }}/秒</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 情绪曲线 -->
      <div class="metric-card emotion-card">
        <div class="metric-header">
          <h3>💭 文案情绪曲线</h3>
        </div>
        <div class="metric-body">
          <div class="emotion-chart">
            <canvas ref="emotionCanvas" height="120"></canvas>
          </div>
          <p class="metric-description">{{ emotionSummary }}</p>
        </div>
      </div>

      <!-- 完播率预测 -->
      <div class="metric-card completion-card">
        <div class="metric-header">
          <h3>⏱️ 完播率预测</h3>
        </div>
        <div class="metric-body">
          <div class="completion-display">
            <div class="completion-bar">
              <div 
                class="completion-fill"
                :style="{ width: `${completionRate}%` }"
              >
                <span class="completion-text">{{ completionRate }}%</span>
              </div>
            </div>
          </div>
          <div class="completion-factors">
            <div 
              v-for="(factor, index) in completionFactors" 
              :key="index"
              class="factor-item"
            >
              {{ factor }}
            </div>
          </div>
        </div>
      </div>

      <!-- 爆款概率 -->
      <div class="metric-card viral-card highlight">
        <div class="metric-header">
          <h3>🎯 爆款概率</h3>
        </div>
        <div class="metric-body">
          <div class="viral-probability">
            <div class="probability-circle">
              <span class="probability-value">{{ Math.round((viralProbability || 0) * 100) }}%</span>
            </div>
            <div class="probability-level">{{ getViralProbabilityLevel(viralProbability || 0) }}</div>
          </div>
          <div class="top-factors">
            <h4>关键因素</h4>
            <div 
              v-for="factor in topFactors" 
              :key="factor.name"
              class="factor-row"
            >
              <span class="factor-name">{{ factor.name }}</span>
              <div class="factor-bar">
                <div 
                  class="factor-fill"
                  :style="{ width: `${factor.score}%` }"
                ></div>
              </div>
              <span class="factor-score">+{{ factor.score }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'

interface Props {
  hookStrength?: number
  hookAnalysis?: string
  rhythmDensity?: number
  cutFrequency?: number
  rhythmTimeline?: Array<{timestamp: number, intensity: number}>
  emotionCurve?: Array<{timestamp: number, value: number}>
  emotionSummary?: string
  completionRate?: number
  completionFactors?: string[]
  viralProbability?: number
  topFactors?: Array<{name: string, score: number, description: string}>
}

const props = defineProps<Props>()

const rhythmCanvas = ref<HTMLCanvasElement>()
const emotionCanvas = ref<HTMLCanvasElement>()

const hasMetrics = computed(() => {
  return props.hookStrength !== undefined || 
         props.rhythmDensity !== undefined ||
         props.completionRate !== undefined
})

const getHookLevel = (score: number): string => {
  if (score >= 8) return '强力吸引'
  if (score >= 6) return '较好吸引'
  if (score >= 4) return '一般吸引'
  return '吸引力弱'
}

const getViralProbabilityLevel = (prob: number): string => {
  if (prob >= 0.7) return '极高爆款潜力'
  if (prob >= 0.5) return '高爆款潜力'
  if (prob >= 0.3) return '中等爆款潜力'
  return '较低爆款潜力'
}

const getRingOffset = (value: number, max: number): number => {
  const circumference = 2 * Math.PI * 45
  const progress = value / max
  return circumference * (1 - progress)
}

const drawRhythmChart = () => {
  if (!rhythmCanvas.value || !props.rhythmTimeline) return
  
  const canvas = rhythmCanvas.value
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  
  const width = canvas.width = canvas.offsetWidth
  const height = canvas.height
  
  ctx.clearRect(0, 0, width, height)
  
  // 绘制网格
  ctx.strokeStyle = '#e0e0e0'
  ctx.lineWidth = 1
  for (let i = 0; i <= 10; i++) {
    const y = (height / 10) * i
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(width, y)
    ctx.stroke()
  }
  
  // 绘制节奏曲线
  const data = props.rhythmTimeline
  if (data.length < 2) return
  
  ctx.strokeStyle = '#667eea'
  ctx.lineWidth = 3
  ctx.beginPath()
  
  data.forEach((point, index) => {
    const x = (width / (data.length - 1)) * index
    const y = height - (point.intensity / 10) * height
    
    if (index === 0) {
      ctx.moveTo(x, y)
    } else {
      ctx.lineTo(x, y)
    }
  })
  
  ctx.stroke()
  
  // 填充渐变
  ctx.lineTo(width, height)
  ctx.lineTo(0, height)
  ctx.closePath()
  
  const gradient = ctx.createLinearGradient(0, 0, 0, height)
  gradient.addColorStop(0, 'rgba(102, 126, 234, 0.3)')
  gradient.addColorStop(1, 'rgba(102, 126, 234, 0.05)')
  ctx.fillStyle = gradient
  ctx.fill()
}

const drawEmotionChart = () => {
  if (!emotionCanvas.value || !props.emotionCurve) return
  
  const canvas = emotionCanvas.value
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  
  const width = canvas.width = canvas.offsetWidth
  const height = canvas.height
  
  ctx.clearRect(0, 0, width, height)
  
  // 绘制中线
  ctx.strokeStyle = '#e0e0e0'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(0, height / 2)
  ctx.lineTo(width, height / 2)
  ctx.stroke()
  
  // 绘制情绪曲线
  const data = props.emotionCurve
  if (data.length < 2) return
  
  ctx.strokeStyle = '#ff6b6b'
  ctx.lineWidth = 3
  ctx.beginPath()
  
  data.forEach((point, index) => {
    const x = (width / (data.length - 1)) * index
    const y = height - (point.value / 10) * height
    
    if (index === 0) {
      ctx.moveTo(x, y)
    } else {
      ctx.lineTo(x, y)
    }
  })
  
  ctx.stroke()
}

onMounted(() => {
  drawRhythmChart()
  drawEmotionChart()
})

watch(() => [props.rhythmTimeline, props.emotionCurve], () => {
  drawRhythmChart()
  drawEmotionChart()
})
</script>

<style scoped>
.enhanced-viral-section {
  margin-top: 2rem;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.metric-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: transform 0.2s;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.metric-card.highlight {
  border: 2px solid #667eea;
}

.metric-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem;
}

.metric-header h3 {
  margin: 0;
  font-size: 1.1rem;
}

.metric-body {
  padding: 1.5rem;
}

/* 开场钩子 */
.score-display {
  text-align: center;
}

.score-ring {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 1rem;
}

.score-ring svg {
  transform: rotate(-90deg);
}

.ring-bg {
  fill: none;
  stroke: #e0e0e0;
  stroke-width: 8;
}

.ring-progress {
  fill: none;
  stroke: #667eea;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: 283;
  transition: stroke-dashoffset 1s ease;
}

.score-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.score-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #667eea;
}

.score-max {
  font-size: 1rem;
  color: #999;
}

.score-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
}

/* 节奏密度 */
.rhythm-chart, .emotion-chart {
  position: relative;
  width: 100%;
  margin-bottom: 1rem;
}

.rhythm-chart canvas, .emotion-chart canvas {
  width: 100%;
  display: block;
}

.rhythm-stats {
  display: flex;
  justify-content: space-around;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.25rem;
}

.stat-value {
  display: block;
  font-size: 1.2rem;
  font-weight: 600;
  color: #667eea;
}

/* 完播率 */
.completion-display {
  margin-bottom: 1rem;
}

.completion-bar {
  height: 40px;
  background: #e0e0e0;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
}

.completion-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50 0%, #8bc34a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width 1s ease;
}

.completion-text {
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
}

.completion-factors {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.factor-item {
  padding: 0.5rem;
  background: #f5f5f5;
  border-radius: 6px;
  font-size: 0.9rem;
}

/* 爆款概率 */
.viral-probability {
  text-align: center;
  margin-bottom: 1.5rem;
}

.probability-circle {
  width: 120px;
  height: 120px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

.probability-value {
  font-size: 2.5rem;
  font-weight: bold;
  color: white;
}

.probability-level {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.top-factors h4 {
  margin: 0 0 1rem 0;
  color: #333;
}

.factor-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.factor-name {
  min-width: 80px;
  font-size: 0.9rem;
  color: #666;
}

.factor-bar {
  flex: 1;
  height: 20px;
  background: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
}

.factor-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 1s ease;
}

.factor-score {
  min-width: 40px;
  text-align: right;
  font-weight: 600;
  color: #667eea;
}

.metric-description {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
}
</style>
